// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to create default working hours
  const createDefaultWorkingHours = async (userId) => {
    try {
      const defaultHours = Array.from({ length: 7 }, (_, i) => ({
        user_id: userId,
        day_of_week: i,
        start_time: '09:00',
        end_time: '17:00',
        is_active: i > 0 && i < 6,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('working_hours')
        .upsert(defaultHours, {
          onConflict: 'user_id,day_of_week',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error setting default working hours:', error);
      }
    } catch (error) {
      console.error('Error in createDefaultWorkingHours:', error);
    }
  };

  useEffect(() => {
    // Function to load user from localStorage
    const loadUserFromLocalStorage = () => {
      try {
        const storedUser = localStorage.getItem('authUser');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load user from localStorage:', error);
      }
    };

    // Call the function to load user from localStorage
    loadUserFromLocalStorage();

    // Check active sessions from Supabase
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        await handleUserSession(session);
      }
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        await handleUserSession(session);
        // Persist the user to localStorage
        localStorage.setItem('authUser', JSON.stringify(session.user));
      } else {
        // Remove user from localStorage on logout
        localStorage.removeItem('authUser');
      }
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleUserSession = async (session) => {
    try {
      const { user, provider_token, provider_refresh_token } = session;
      
      if (!user) {
        console.error('No user in session');
        return;
      }

      const { user_metadata } = user;

      const profileData = {
        id: user.id,
        email: user.email,
        name: user_metadata?.full_name || user_metadata?.name || user.email,
        google_calendar_token: provider_token,
        google_refresh_token: provider_refresh_token,
        time_zone: user_metadata?.timezone || 'America/Toronto',
        updated_at: new Date().toISOString()
      };

      // Update or create profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert(profileData, {
          onConflict: 'id',
          returning: 'minimal'
        });

      if (profileError) {
        throw profileError;
      }

      // Check if working hours already exist for this user
      const { data: existingHours } = await supabase
        .from('working_hours')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      // Only create default working hours if none exist
      if (!existingHours || existingHours.length === 0) {
        await createDefaultWorkingHours(user.id);
      }

      // Store the updated user in localStorage
      localStorage.setItem('authUser', JSON.stringify(user));

    } catch (error) {
      console.error('Error in handleUserSession:', error);
    }
  };

  /**
   * Method to check if a session is stored.
   * Returns true if a session exists, false otherwise.
   */
  const isSessionStored = async () => {
    // Option 1: Check localStorage
    const storedUser = localStorage.getItem('authUser');
    console.log(storedUser);
    if (storedUser) return true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, isSessionStored }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};